import { useState } from 'react';
import { X, ShoppingCart, Loader2, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    useGenerateShoppingListFromRecipes,
    useShoppingListSummaries,
    type RecipeShoppingSelection,
} from '@/services/shoppingLists';
import { cn } from '@/lib/utils';

interface AddToShoppingListDialogProps {
    open: boolean;
    onClose: () => void;
    recipes: RecipeShoppingSelection[];
    defaultName?: string;
    onAdded?: () => void; // called after a successful add (e.g. to navigate away)
}

const AddToShoppingListDialog = ({
    open,
    onClose,
    recipes,
    defaultName = '',
    onAdded,
}: AddToShoppingListDialogProps) => {
    const [mode, setMode] = useState<'new' | 'existing'>('new');
    const [name, setName] = useState(defaultName);
    const [targetListId, setTargetListId] = useState('');

    const generate = useGenerateShoppingListFromRecipes();
    // Only fetch existing lists once the dialog is open.
    const {
        data: lists = [],
        isLoading: listsLoading,
        isError: listsFailed,
        isFetching: listsFetching,
        refetch: refetchLists,
    } = useShoppingListSummaries(open);

    if (!open) return null;

    const handleConfirm = async () => {
        if (recipes.length === 0) {
            toast.error('No recipes selected');
            return;
        }
        if (mode === 'existing' && !targetListId) {
            toast.error('Pick a list to add to');
            return;
        }

        try {
            await generate.mutateAsync({
                recipes,
                name: mode === 'new' ? name.trim() || undefined : undefined,
                shoppingListId: mode === 'existing' ? targetListId : undefined,
            });
            toast.success(
                mode === 'new' ? 'Shopping list created!' : 'Added to your shopping list!',
            );
            onClose();
            onAdded?.();
        } catch {
            toast.error('Failed to add to shopping list');
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            onClick={onClose}
        >
            <div
                className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="mb-4 flex items-center justify-between">
                    <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                        <ShoppingCart className="h-5 w-5 text-emerald-600" />
                        Add to Shopping List
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 transition-colors hover:text-gray-600"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <p className="mb-4 text-sm text-gray-600">
                    {recipes.length === 1
                        ? 'Ingredients from this recipe will be added.'
                        : `Ingredients from ${recipes.length} recipes will be combined.`}
                </p>

                {/* Mode toggle */}
                <div className="mb-4 grid grid-cols-2 gap-2">
                    <button
                        type="button"
                        onClick={() => setMode('new')}
                        className={cn(
                            'rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
                            mode === 'new'
                                ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                                : 'border-gray-200 text-gray-600 hover:bg-gray-50',
                        )}
                    >
                        New list
                    </button>
                    <button
                        type="button"
                        onClick={() => setMode('existing')}
                        className={cn(
                            'rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
                            mode === 'existing'
                                ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                                : 'border-gray-200 text-gray-600 hover:bg-gray-50',
                        )}
                    >
                        Existing list
                    </button>
                </div>

                {mode === 'new' ? (
                    <div className="mb-6">
                        <Label htmlFor="newListName">List name</Label>
                        <Input
                            id="newListName"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={defaultName || 'My Shopping List'}
                            className="mt-1"
                        />
                    </div>
                ) : (
                    <div className="mb-6">
                        <Label htmlFor="existingList">Choose a list</Label>
                        {listsLoading ? (
                            <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
                                <Loader2 className="h-4 w-4 animate-spin" /> Loading your lists…
                            </div>
                        ) : listsFailed ? (
                            // A failed load must not read as "no lists": that is
                            // how #352 hid every list behind the empty state.
                            <div
                                role="alert"
                                className="mt-2 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                            >
                                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                                <div className="flex-1">
                                    <p>We couldn't load your lists.</p>
                                    <button
                                        type="button"
                                        onClick={() => refetchLists()}
                                        disabled={listsFetching}
                                        className="mt-1 font-medium text-red-800 underline underline-offset-2 hover:text-red-900 disabled:opacity-60"
                                    >
                                        {listsFetching ? 'Trying again…' : 'Try again'}
                                    </button>
                                </div>
                            </div>
                        ) : lists.length === 0 ? (
                            <p className="mt-2 text-sm text-gray-500">
                                You have no active lists yet. Create a new one instead.
                            </p>
                        ) : (
                            <select
                                id="existingList"
                                value={targetListId}
                                onChange={(e) => setTargetListId(e.target.value)}
                                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-emerald-500"
                            >
                                <option value="">Select a list…</option>
                                {lists.map((list) => (
                                    <option key={list.id} value={list.id}>
                                        {list.name}
                                        {list._count ? ` (${list._count.items} items)` : ''}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>
                )}

                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={generate.isPending}
                        className="bg-emerald-600 text-white hover:bg-emerald-700"
                    >
                        {generate.isPending ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Adding…
                            </>
                        ) : mode === 'new' ? (
                            'Create list'
                        ) : (
                            'Add to list'
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default AddToShoppingListDialog;
